﻿/** Utility class for manipulating strings */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class StringUtil {
    /**
     * Returns a value indicating whether a string value is null, empty or undefined
     * @param value the value to check
     */
    static isNullOrEmpty(value: string): boolean {
        return value === `` || value === null || value === undefined;
    }

    /**
     * Returns a value indicating whether a string value is null or undefined
     * @param value the value to check
     */
    static isNull(value: string): boolean {
        return value === null || value === undefined;
    }

    /**
     * Returns the input string or a default value if the input string is null
     * @param value the value to exstract
     * @param defaultValue the default value to use if no value is found
     */
    static getValueOrDefault(value: string, defaultValue: string): string {
        return StringUtil.isNull(value) ? defaultValue : value;
    }

    /**
     * Takes an array of strings and appends them together using the supplied concaternatation value
     * @param values the strings to concaternate
     * @param concaternationValue the string that should appear between each concaternation
     */
    static concaternateStrings(
        values: string[],
        concaternationValue: string,
    ): string {
        // Don't process null values
        if (
            values === null ||
            typeof values === `undefined` ||
            values.length === 0
        ) {
            return ``;
        }

        // Use an empty string if the concaternation value is not supplied
        const concatValue: string = StringUtil.getValueOrDefault(
            concaternationValue,
            ``,
        );

        // Used to build the string
        let output = ``;

        // Build the output string
        for (let i = 0; i < values.length; i++) {
            // If the value is not empty then append it to the output string
            if (!StringUtil.isNullOrEmpty(values[i])) {
                // Append value to output
                output += values[i];

                // If not last item then add the concaternation string
                if (i < values.length - 1) {
                    output += concatValue;
                }
            }
        }

        return output;
    }

    /**
     * Returns the array of characters respecting Unicode outside the BMP (Basic Multilingual Plane).
     *
     * This means that characters beyond the BMP which are in two parts, called a "Surrogate Pair", are
     * maintained as one character - this would not be the case when using charAt or indexing a string.
     *
     * @param value The string to get the characters from.
     */
    static chars(value: string): string[] {
        const chars: string[] = [];

        for (let i = 0; i < value.length; i++) {
            let ret = value.charAt(i);

            if (
                /[\uDB800-\uDBFF]/.test(ret) &&
                /[\uDC00-\uDFFF]/.test(value.charAt(i + 1))
            ) {
                ret += value.charAt(++i);
            }
            chars.push(ret);
        }

        return chars;
    }

    static hasLowercase(value: string): boolean {
        for (const char of value) {
            if (
                char === char.toLocaleLowerCase() &&
                char !== char.toLocaleUpperCase()
            ) {
                return true;
            }
        }
        return false;
    }

    static hasUppercase(value: string): boolean {
        for (const char of value) {
            if (
                char === char.toLocaleUpperCase() &&
                char !== char.toLocaleLowerCase()
            ) {
                return true;
            }
        }
        return false;
    }
}
